import React, { useState, useEffect } from 'react';
import { detailDossierConfigAPI } from '../../services/api';
import { 
  FileText, Eye, EyeOff, GripVertical, Save, 
  RefreshCw, ChevronUp, ChevronDown, Check, Edit3
} from 'lucide-react';

const WidgetDetailDossierConfig = () => {
  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await detailDossierConfigAPI.getElements();
      const sortedElements = [...res.data.elements].sort((a, b) => a.ordre - b.ordre);
      setElements(sortedElements);
    } catch (error) {
      console.error('Erreur chargement config:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleElement = (code) => {
    setElements(elements.map(el => 
      el.code === code ? { ...el, visible: !el.visible } : el
    ));
    setHasChanges(true);
  };

  const moveElement = (index, direction) => {
    const newElements = [...elements];
    const newIndex = index + direction;
    
    if (newIndex < 0 || newIndex >= elements.length) return;
    
    [newElements[index], newElements[newIndex]] = [newElements[newIndex], newElements[index]];
    newElements.forEach((el, i) => { el.ordre = i; });
    
    setElements(newElements);
    setHasChanges(true);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await detailDossierConfigAPI.updateElements(elements);
      setHasChanges(false);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const resetConfig = async () => {
    if (!window.confirm('Réinitialiser la configuration par défaut ?')) return;
    try {
      await detailDossierConfigAPI.resetElements();
      await loadConfig();
      setHasChanges(false);
    } catch (error) {
      console.error('Erreur reset:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const visibleCount = elements.filter(e => e.visible).length;
  const hiddenCount = elements.filter(e => !e.visible).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-green-600" />
            Configuration "Détail du dossier"
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Choisissez les éléments visibles dans l'onglet "Détail du dossier" de la fiche client
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={resetConfig}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 px-3 py-2"
          >
            <RefreshCw className="w-4 h-4" />
            Réinitialiser
          </button>
          {hasChanges && (
            <button
              onClick={saveConfig}
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="save-detail-config"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </button>
          )}
        </div>
      </div>

      {/* Explication */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <p>
          <strong>Mode d'emploi :</strong> Activez/désactivez les éléments avec l'icône œil. 
          Les éléments marqués <Edit3 className="w-4 h-4 inline text-blue-600" /> sont éditables directement depuis la fiche client.
        </p>
      </div>

      {/* Liste des éléments */}
      <div className="space-y-2">
        {elements.map((el, index) => (
          <div
            key={el.code}
            className={`bg-white rounded-lg border-2 transition ${
              el.visible 
                ? 'border-green-200 shadow-sm' 
                : 'border-gray-200 opacity-60'
            }`}
          >
            <div className="p-4 flex items-center gap-4">
              {/* Contrôles d'ordre */}
              <div className="flex flex-col items-center gap-1">
                <button
                  onClick={() => moveElement(index, -1)}
                  disabled={index === 0}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <GripVertical className="w-5 h-5 text-gray-300" />
                <button
                  onClick={() => moveElement(index, 1)}
                  disabled={index === elements.length - 1}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>

              {/* Numéro d'ordre */}
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600">
                {index + 1}
              </div>

              {/* Infos élément */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">{el.label}</h3>
                  {el.editable && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1">
                      <Edit3 className="w-3 h-3" />
                      Éditable
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500">Code : {el.code}</p>
              </div>

              {/* Toggle visibilité */}
              <button
                onClick={() => toggleElement(el.code)}
                className={`p-3 rounded-lg transition ${
                  el.visible 
                    ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                }`}
                title={el.visible ? 'Masquer' : 'Afficher'}
                data-testid={`toggle-element-${el.code}`}
              >
                {el.visible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <Eye className="w-4 h-4 text-green-600" />
            <strong>{visibleCount}</strong> éléments visibles
          </span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <EyeOff className="w-4 h-4 text-gray-400" />
            <strong>{hiddenCount}</strong> éléments masqués
          </span>
        </div>
      </div>

      {/* Avertissement changements non sauvegardés */}
      {hasChanges && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
          <p className="text-amber-800 text-sm font-medium">
            Vous avez des modifications non enregistrées
          </p>
          <button
            onClick={saveConfig}
            disabled={saving}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Enregistrer maintenant
          </button>
        </div>
      )}
    </div>
  );
};

export default WidgetDetailDossierConfig;
